import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Row } from "react-bootstrap";
import uniqBy from "lodash/uniqBy";

import Button from "../Button";
import Modal from "../Modal";
import FolderTree from "./FolderTree";
import "./styles.scss";

const getActiveFoldersTree = tree => {
  const result = [tree];
  const nestedTree = tree.dirs.find(item => item?.dirs?.length);
  if (nestedTree) {
    result.push(...getActiveFoldersTree(nestedTree));
  }

  return result;
};

const FolderTreeModal = ({
  title = "Please select a folder",
  onClose,
  getUserDirectories,
  getUserTrees,
  onDirectoryPick,
  initialTreePath,
  initialFolders,
  setInitialFolders,
  ...modalProps
}) => {
  const [activeKey, setActiveKey] = useState("");
  const [isAnyFolderPicked, setIsAnyFolderPicked] = useState(false);
  const [activeFolders, setActiveFolders] = useState([]);

  const closeModal = useCallback(() => {
    setActiveKey("");
    setIsAnyFolderPicked(false);
    setActiveFolders([]);
    setInitialFolders([]);
    onClose();
  }, [
    setActiveKey,
    setIsAnyFolderPicked,
    setActiveFolders,
    setInitialFolders,
    onClose,
  ]);

  useEffect(() => {
    // Note: Effect callbacks are synchronous
    const setupData = async () => {
      if (modalProps.show) {
        try {
          const userTree = await getUserTrees(initialTreePath);
          const activeFoldersTree = getActiveFoldersTree(userTree);
          setActiveKey(initialTreePath);
          setActiveFolders(
            uniqBy([initialFolders, ...activeFoldersTree], "path")
          );
        } catch (e) {
          setActiveKey(initialFolders.path);
          setActiveFolders([initialFolders]);
        }
      }
    };
    setupData();
  }, [
    initialTreePath,
    initialFolders,
    getUserTrees,
    setActiveKey,
    setActiveFolders,
    modalProps.show,
  ]);

  return (
    <Modal
      size="lg"
      animation={false}
      {...modalProps}
      dialogClassName={classNames("mydpd-folder-tree", modalProps.className)}
      onHide={onClose}
    >
      <Modal.Header
        className={classNames("modal-header-text", modalProps.headerClassName)}
      >
        {title}
      </Modal.Header>
      <Modal.Body
        className={classNames("modal-body-text", modalProps.bodyClassName)}
      >
        <Row className={classNames("flex-nowrap m-0", "tree-row")}>
          <FolderTree
            activeFolders={activeFolders}
            setActiveFolders={setActiveFolders}
            getUserDirectories={getUserDirectories}
            activeKey={activeKey}
            setActiveKey={setActiveKey}
            setIsAnyFolderPicked={setIsAnyFolderPicked}
            initialPath={activeKey}
          />
        </Row>
      </Modal.Body>
      <Modal.Footer
        className={classNames("modal-footer", modalProps.footerClassName)}
      >
        <Button.Toolbar className="justify-content-end m-0">
          <Button variant="dark" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              onDirectoryPick(activeKey);
              closeModal();
            }}
            disabled={!isAnyFolderPicked}
          >
            Select Folder
          </Button>
        </Button.Toolbar>
      </Modal.Footer>
    </Modal>
  );
};

export const FolderTreeModalProps = (FolderTreeModal.propTypes = {
  initialFolders: PropTypes.shape({
    dirs: PropTypes.array,
    path: PropTypes.string,
    name: PropTypes.string,
  }),
  title: PropTypes.string,
  initialTreePath: PropTypes.string.isRequired,
  onClose: PropTypes.func,
  getUserDirectories: PropTypes.func,
  getUserTrees: PropTypes.func,
  onDirectoryPick: PropTypes.func,
  setInitialFolders: PropTypes.func,
});

FolderTree.Modal = FolderTreeModal;

export default FolderTree;
